import Header from '@carvana/header';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import React, { memo, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Summary } from '|/app/services';
import UserInitializationContext from '|/context/user-initialization-context';
import styles from './header-mfe.module.css';

interface HeaderMfeProps {
  orderSummary?: Summary | null;
}

const getChatContext = (vehicleId: number | null = null) => ({
  purchase: {
    vehicle: {
      vehicleId
    }
  }
});

type ChatContext = ReturnType<typeof getChatContext>;

interface HeaderComponentProps {
  environment: string | undefined;
  isLoggedInExternally: boolean;
  purchaseProcessVehiclePrice: number | null;
  useProvider: boolean;
  currentRoute: string;
  chatContext: ChatContext | null;
  shouldLoadPlugin: boolean;
}

type HeaderComponent = React.ComponentType<HeaderComponentProps>;

// @carvana/header ships no types (global.d.ts declares it as any).
const HeaderSkeleton = Header as HeaderComponent;

interface HeadProps {
  Component?: HeaderComponent;
  isLoggedIn: boolean;
  vehicleTotalPrice: number | null;
  currentRoute: string;
  chatContext: ChatContext | null;
}

const Head = memo<HeadProps>(
  ({ Component = HeaderSkeleton, isLoggedIn, vehicleTotalPrice, currentRoute, chatContext }) => (
    <Component
      environment={process.env.CVNA_APP_ENVIRONMENT}
      isLoggedInExternally={isLoggedIn}
      purchaseProcessVehiclePrice={vehicleTotalPrice}
      useProvider
      currentRoute={currentRoute}
      chatContext={chatContext}
      shouldLoadPlugin={false}
    />
  )
);
Head.displayName = 'Head';

const HeaderMfe: React.FC<HeaderMfeProps> = ({ orderSummary = null }) => {
  const {
    vehicle = null,
    vehicle: { id: vehicleId = null } = {},
    totals: { totalPrice: vehicleTotalPrice = null } = {}
  } = orderSummary || {};

  const { accessToken } = useContext(UserInitializationContext);
  const isLoggedIn = accessToken?.user?.name !== undefined;
  const location = useLocation();

  const currentRoute = (location && location.pathname) || '';
  const hasVehicle = vehicle !== null;
  const chatContext = useMemo(
    () => (hasVehicle ? getChatContext(vehicleId) : null),
    [hasVehicle, vehicleId]
  );

  const headProps = { isLoggedIn, vehicleTotalPrice, currentRoute, chatContext };

  return (
    <div className={styles.headerWrapper}>
      <MfeReactComponent
        component="default"
        module="HeaderModule"
        loading={<Head {...headProps} />}
      >
        {({ Component: RemoteHeader }: { Component: HeaderComponent }) => (
          <Head Component={RemoteHeader} {...headProps} />
        )}
      </MfeReactComponent>
    </div>
  );
};

export default HeaderMfe;
